import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [token, setToken] = useState('');
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [feedback, setFeedback] = useState({ type: '', message: '' });
  const [errors, setErrors] = useState({});
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Extract token from URL query parameter
    const queryParams = new URLSearchParams(location.search);
    const tokenFromUrl = queryParams.get('id');
    
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
      verifyToken(tokenFromUrl);
    } else {
      setIsVerifying(false);
      setFeedback({
        type: 'error',
        message: 'Токен для сброса пароля не был предоставлен'
      });
    }
  }, [location]);

  const verifyToken = async (token) => {
    setIsVerifying(true);
    
    try {
      const response = await fetch('/api/v1/verify_recovery_token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsTokenValid(true);
        setEmail(data.email);
      } else {
        setFeedback({
          type: 'error',
          message: data.message || 'Недействительный или истекший токен для сброса пароля'
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Произошла ошибка при проверке токена'
      });
      console.error('Error verifying token:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'password') {
      setPassword(value);
    } else if (name === 'passwordConfirm') {
      setPasswordConfirm(value);
    }
    
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear feedback when user starts typing again
    if (feedback.message) {
      setFeedback({ type: '', message: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!password.trim()) {
      newErrors.password = 'Обязательное поле';
    } else if (password.length < 6) {
      newErrors.password = 'Пароль должен содержать не менее 6 символов';
    }
    
    if (!passwordConfirm.trim()) {
      newErrors.passwordConfirm = 'Обязательное поле';
    } else if (password !== passwordConfirm) {
      newErrors.passwordConfirm = 'Пароли не совпадают';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setFeedback({ type: '', message: '' });
    
    try {
      const response = await fetch('/api/v1/recover_password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          recover_password: password,
          recover_password_repeat: passwordConfirm
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setFeedback({
          type: 'success',
          message: data.message || 'Пароль успешно изменен'
        });
        
        // Wait a bit before redirecting to login
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setFeedback({
          type: 'error',
          message: data.message || 'Не удалось изменить пароль'
        });
      }
    } catch (error) {
      setFeedback({
        type: 'error',
        message: 'Произошла ошибка при сбросе пароля'
      });
      console.error('Error resetting password:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="flex justify-center my-5 py-5">
          <div className="w-full md:w-2/3 lg:w-1/2 bg-gray-200 py-5 px-5 rounded-lg pb-10 flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#c0834d]"></div>
            <p className="ml-3 text-gray-700">Проверка токена...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex justify-center my-5 py-5">
        <div className="w-full md:w-2/3 lg:w-1/2 bg-gray-200 py-5 px-5 rounded-lg pb-10">
          {feedback.message && (
            <div className={`mb-4 p-3 rounded ${feedback.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
              {feedback.message}
            </div>
          )}
          
          {isTokenValid ? (
            <form onSubmit={handleSubmit} className="w-full">
              <div className="flex flex-col items-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-6">Создание нового пароля</h2>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <label htmlFor="email" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                    Эл. почта
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    className="w-full p-2 border rounded my-3 bg-gray-100"
                    disabled
                  />
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <label htmlFor="password" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                    Новый пароль
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={handleChange}
                    placeholder="Новый пароль"
                    className={`w-full p-2 border rounded my-3 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    required
                  />
                  {errors.password && (
                    <div className="text-red-500 text-sm mt-1">{errors.password}</div>
                  )}
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <label htmlFor="passwordConfirm" className="text-[#544a28] text-[13px] lg:text-base font-bold font-['Open Sans']">
                    Подтверждение пароля
                  </label>
                  <input
                    type="password"
                    id="passwordConfirm"
                    name="passwordConfirm"
                    value={passwordConfirm}
                    onChange={handleChange}
                    placeholder="Повторите пароль"
                    className={`w-full p-2 border rounded my-3 ${errors.passwordConfirm ? 'border-red-500' : 'border-gray-300'}`}
                    required
                  />
                  {errors.passwordConfirm && (
                    <div className="text-red-500 text-sm mt-1">{errors.passwordConfirm}</div>
                  )}
                </div>
                
                <div className="w-full px-0 lg:px-3 mb-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-2 px-4 bg-[#c0834d] text-white rounded hover:bg-[#a87442] transition-colors ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Отправка...' : 'Изменить пароль'}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div className="w-full text-center">
              <Link to="/forgot_password" className="text-blue-600 hover:underline">
                Запросить новую ссылку для сброса пароля
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword; 